import { useEffect, useState } from "react";

import { getProethicaTestByKind } from "@constants/demo/proethica-tests";
import { useProethicaNextModule } from "@features/dashboard/hooks/useProethicaNextModule";
import {
	useReviewSession,
	useSession,
	useSessionStore,
} from "@features/session";
import { useProethicaSessionStore } from "@features/session/hooks/useProethicaSessionStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, CardContainer, useDevice } from "@memorang/ui";
import { ProethicaTestBadge } from "components/ProethicaTestBadge";
import { router } from "expo-router";
import { Text } from "react-native-paper";
import {
	resolveProethicaNextEntryMeta,
	resolveProethicaReportNextStep,
} from "../../helpers/proethica-next-entry";
import {
	buildProethicaTestReportCopy,
	buildProethicaTestStatTiles,
	computeProethicaTestScore,
	isProethicaTestPassed,
	resolveProethicaTestReportComposition,
	sumProethicaTestTimeSeconds,
} from "../../helpers/proethica-test-report";
import {
	ProethicaReportFooter,
	type ReportCta,
} from "../ProethicaReportFooter";
import { ProethicaReportShell } from "../ProethicaReportShell";
import { NextEntryCard } from "../ProethicaReportView/NextEntryCard";
import { ReportHeroCard } from "../ProethicaReportView/ReportHeroCard";
import { ReportStatTiles } from "../ProethicaReportView/ReportStatTiles";
import { ProethicaPretestComparisonCard } from "./ProethicaPretestComparisonCard";
import { ScoreDonut } from "./ScoreDonut";

const HERO_BADGE_SIZE = 80;

// Score-only report for pre/post tests: reuses the module report's shell, hero,
// and next-entry card, minus analytics or topic breakdown. Per-variant card and
// footer choices live in resolveProethicaTestReportComposition.
const ProethicaTestReport = ({
	pretestScore,
}: {
	pretestScore: number | null;
}) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const sessionItems = useSessionStore((state) => state.sessionItems);
	const itemStates = useSessionStore((state) => state.itemStates);
	const proethicaTestKind = useSessionStore((state) => state.proethicaTestKind);
	const moduleId = useSessionStore((state) => state.moduleId);
	const resetSessionStore = useSessionStore((state) => state.resetSessionStore);
	const { handleReviewModule, isPreparingReview } = useReviewSession();

	const setPreTestCompleted = useProethicaSessionStore(
		(state) => state.setPreTestCompleted,
	);
	const setPostTestPassed = useProethicaSessionStore(
		(state) => state.setPostTestPassed,
	);

	const { handleStartProethicaTest } = useSession();
	const {
		nextEntry,
		isResolvingPath,
		continueLabel,
		handleContinueToNextModule,
		isContinueLoading,
	} = useProethicaNextModule(moduleId);
	const [retaking, setRetaking] = useState(false);

	const descriptor = getProethicaTestByKind(proethicaTestKind);
	const { numCorrect, numTotal, percentage } = computeProethicaTestScore(
		sessionItems,
		itemStates,
	);

	const statTiles = buildProethicaTestStatTiles({
		numTotal,
		totalTimeSeconds: sumProethicaTestTimeSeconds(sessionItems, itemStates),
	});

	const isPretest = proethicaTestKind === "pretest";
	const isPostTest = proethicaTestKind === "posttest";
	const passed = isProethicaTestPassed(percentage);

	const showPretestComparison = isPostTest && pretestScore != null;

	const nextStep = resolveProethicaReportNextStep(nextEntry, isResolvingPath);
	const composition = resolveProethicaTestReportComposition({
		isPretest,
		passed,
		nextEntryIsHome: nextStep.kind === "home",
	});

	useEffect(() => {
		if (isPretest) {
			setPreTestCompleted(true);
		}
		if (isPostTest && passed) {
			setPostTestPassed(true);
		}
	}, [isPretest, isPostTest, passed, setPreTestCompleted, setPostTestPassed]);

	const handleGoHome = () => {
		resetSessionStore();
		router.replace("/(protected)/(tabs)/home");
	};

	const handleRetake = async () => {
		setRetaking(true);
		try {
			await handleStartProethicaTest({
				testId: String(descriptor?.collectionId),
				replace: true,
			});
		} finally {
			setRetaking(false);
		}
	};

	const typeLabel = descriptor?.typeLabel ?? "Test";
	const copy = buildProethicaTestReportCopy(isPretest, passed);

	const footerPrimaryCta: ReportCta | undefined =
		composition.footerPrimary === "review"
			? {
					label: "Review answers",
					icon: "clipboard-text-search-outline",
					loading: isPreparingReview,
					onPress: handleReviewModule,
				}
			: composition.footerPrimary === "retake"
				? {
						label: "Retake post-test",
						icon: "refresh",
						loading: retaking,
						onPress: handleRetake,
					}
				: undefined;

	return (
		<ProethicaReportShell
			footer={
				<ProethicaReportFooter
					primaryCta={footerPrimaryCta}
					handleGoHome={handleGoHome}
					goHomePrimary={composition.goHomePrimary}
				/>
			}
		>
			<ReportHeroCard
				moduleLabel={typeLabel}
				badge={
					<ProethicaTestBadge
						kind={proethicaTestKind ?? "posttest"}
						size={HERO_BADGE_SIZE}
					/>
				}
				title={copy.title}
				summaryText={copy.description}
			/>

			<ReportStatTiles tiles={statTiles} />

			{composition.showNextEntryCard ? (
				<NextEntryCard
					meta={
						nextStep.kind === "entry"
							? resolveProethicaNextEntryMeta(nextStep.entry)
							: undefined
					}
					continueLabel={continueLabel}
					isContinueLoading={isContinueLoading}
					handleContinue={handleContinueToNextModule}
				/>
			) : null}

			<Box
				flexDirection={isMobile ? "column" : "row"}
				alignItems="stretch"
				style={{ gap: 8 }}
			>
				<Box flex={isMobile ? undefined : 1}>
					<CardContainer
						title={copy.scoreTitle}
						subtitle={`Across all ${numTotal} questions`}
						icon="chart-box-outline"
						style={{ height: "100%" }}
						contentStyle={{ flex: 1 }}
					>
						<Box
							flex={1}
							paddingHorizontal={24}
							paddingVertical={40}
							alignItems="center"
							style={{ gap: 20 }}
						>
							<Box flex={1} alignItems="center" justifyContent="center">
								<ScoreDonut
									percentage={percentage}
									color={theme.colors.primary}
									radius={64}
									innerRadius={50}
								>
									<Text
										variant="titleLarge"
										style={{
											color: theme.colors.onSurface,
											fontWeight: "800",
										}}
									>
										{`${numCorrect}/${numTotal}`}
									</Text>
								</ScoreDonut>
							</Box>
							<Text
								variant="titleMedium"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{`${percentage}% correct`}
							</Text>
						</Box>
					</CardContainer>
				</Box>

				{showPretestComparison ? (
					<Box flex={isMobile ? undefined : 1}>
						<ProethicaPretestComparisonCard
							prePercentage={pretestScore}
							postPercentage={percentage}
						/>
					</Box>
				) : null}
			</Box>
		</ProethicaReportShell>
	);
};

export default ProethicaTestReport;
